import type * as React from "react";
import { clamp } from "../../../util/functions/clamp";
import { round } from "../../../util/functions/round";
import type { Angle } from "./Angle";
import {
	CoordinateSpace,
	type ICoordinateSpace,
	type InFullSizeImage,
	type InMonitorSetup,
	type InOverviewImage,
	type InTransformed,
	type InVisibleCanvas,
	type InVisibleCanvasDrawRegion,
	type InWindow,
} from "./CoordinateSpace";
import type { ILine } from "./Line";
import type { ITransformation } from "./Transformation";
import type { IVector } from "./Vector";
import type { BasicPoint } from "./BasicPoint";
import type { ITransformedImageRegionsStore } from "../viewports/viewport-series/image-regions/TransformedImageRegionsStore";
import type { IVisibleCanvasDrawRegionStore } from "../viewports/viewport-series/visible-canvas/VisibleCanvasDrawRegionStore";
import type { IVisibleCanvasStore } from "../viewports/viewport-series/visible-canvas/VisibleCanvasStore";

/**
 * Represents the location (x,y) in a x,y coordinate space
 */
export interface IPoint<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> {
	readonly type: "point";
	readonly coordinateSpace: TCoordinateSpace;
	readonly kind: string;

	readonly x: number;
	readonly y: number;

	/**
	 * Moves the point to a different location.
	 */
	move(vector: IVector<TCoordinateSpace>): IPoint<TCoordinateSpace>;

	/**
	 * Calculates the vector that would need to be added to this point to move to the other point
	 */
	vectorTo(other: IPoint<TCoordinateSpace>): IVector<TCoordinateSpace>;

	/**
	 * Calculates the vector that would need to be added to the other point to move to this point
	 */
	vectorFrom(other: IPoint<TCoordinateSpace>): IVector<TCoordinateSpace>;

	/**
	 * Calculates the point that is exactly in the middle between this point and the other point
	 */
	average(other: IPoint<TCoordinateSpace>): IPoint<TCoordinateSpace>;

	/**
	 * Rounds the x and y value to a specified precision after the comma
	 * @param precision How many numbers of precision there should be after the comma
	 */
	round(precision: number): IPoint<TCoordinateSpace>;

	/**
	 * Clamps this point inside the provided region
	 */
	clamp(region: { topLeft: IPoint<TCoordinateSpace>; bottomRight: IPoint<TCoordinateSpace> }): IPoint<TCoordinateSpace>;

	/**
	 * Calculates the length between two points
	 */
	length(other: IPoint<TCoordinateSpace>): number;

	/**
	 * Gets the perpendicular vector from this point to the provided line.
	 * Note that this method pretends that the line extends infinitely in both directions
	 *
	 * ** NOTE **
	 * This methods returns NULL in the following scenarios:
	 *  - The line has length 0
	 */
	getPerpendicularVectorToLine(line: ILine<TCoordinateSpace>): IVector<TCoordinateSpace> | null;

	/**
	 * Transforms the current point to a new point in the transformed coordinate space.
	 */
	transform(transformation: ITransformation<TCoordinateSpace>): IPoint<InTransformed<TCoordinateSpace>>;

	/**
	 * Rotates this point around the provided center
	 */
	rotate(center: IPoint<TCoordinateSpace>, rotation: Angle): IPoint<TCoordinateSpace>;

	/**
	 * scales a point: multiplies by a scalar
	 */
	scale(scale: number): IPoint<TCoordinateSpace>;

	/**
	 * Creates a BasicPoint that can be used to transfer data to a web worker / serialize to JSON / ...
	 */
	toBasicPoint(): BasicPoint;

	equals(other: IPoint<TCoordinateSpace>): boolean;

	toString(): string;
}

export abstract class Point<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> implements IPoint<TCoordinateSpace> {
	public readonly type: "point";
	abstract readonly kind: string;

	protected constructor(
		public readonly coordinateSpace: TCoordinateSpace,
		public readonly x: number,
		public readonly y: number,
	) {
		this.type = "point";

		if (x === null || x === undefined) throw new Error("X is null or undefined");
		if (y === null || y === undefined) throw new Error("Y is null or undefined");
		if (isNaN(x)) throw new Error("X is NaN");
		if (isNaN(y)) throw new Error("Y is NaN");
	}

	move(vector: IVector<TCoordinateSpace>): IPoint<TCoordinateSpace> {
		return this.coordinateSpace.createPoint(this.x + vector.x, this.y + vector.y);
	}

	vectorTo(other: IPoint<TCoordinateSpace>): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(other.x - this.x, other.y - this.y);
	}

	vectorFrom(other: IPoint<TCoordinateSpace>): IVector<TCoordinateSpace> {
		return this.coordinateSpace.createVector(this.x - other.x, this.y - other.y);
	}

	average(other: IPoint<TCoordinateSpace>): IPoint<TCoordinateSpace> {
		const { x: x1, y: y1 } = this;
		const { x: x2, y: y2 } = other;
		return this.coordinateSpace.createPoint(x1 + (x2 - x1) / 2, y1 + (y2 - y1) / 2);
	}

	length(other: IPoint<TCoordinateSpace>): number {
		const x = this.x - other.x;
		const y = this.y - other.y;
		return Math.sqrt(x * x + y * y);
	}

	equals(other: IPoint<TCoordinateSpace>) {
		return this.kind === other.kind && this.x === other.x && this.y === other.y;
	}

	round(precision: number) {
		const { x, y } = this;

		return this.coordinateSpace.createPoint(round(x, precision), round(y, precision));
	}

	clamp(region: { topLeft: IPoint<TCoordinateSpace>; bottomRight: IPoint<TCoordinateSpace> }): IPoint<TCoordinateSpace> {
		const { topLeft, bottomRight } = region;
		return this.coordinateSpace.createPoint(clamp(this.x, topLeft.x, bottomRight.x), clamp(this.y, topLeft.y, bottomRight.y));
	}

	getPerpendicularVectorToLine(line: ILine<TCoordinateSpace>): IVector<TCoordinateSpace> | null {
		const { from, to } = line;

		const { x: x1, y: y1 } = from;
		const { x: x2, y: y2 } = to;

		// Check if the line does not have length 0
		if (x1 === x2 && y1 === y2) {
			return null;
		}

		// Calculate the line that is perpendicular to the provided line, starting from this point
		const { x: x3, y: y3 } = this;
		const { x: x4, y: y4 } = this.move(line.toVector().perpendicularClockwise());

		const determinant = (x2 - x1) * (y4 - y3) - (x4 - x3) * (y2 - y1);

		// Calculate how far along the line we need to travel to reach the intersection point
		const lambda = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / determinant;

		// Calculate the x and y coordinates of the intersection
		const x = x1 + lambda * (x2 - x1);
		const y = y1 + lambda * (y2 - y1);

		const intersectionPoint = this.coordinateSpace.createPoint(x, y);

		return this.vectorTo(intersectionPoint);
	}

	transform(transformation: ITransformation<TCoordinateSpace>): IPoint<InTransformed<TCoordinateSpace>> {
		const { cos, sin } = Math;
		const { x, y } = this;
		const { origin, rotation, flipHorizontal, flipVertical } = transformation;

		if (!Number.isFinite(x) || !Number.isFinite(y)) {
			return new PointInTransformed(this.coordinateSpace, transformation, x, y);
		}

		//translate origin to the rotation/flipping origin
		let transformedX = x - origin.x;
		let transformedY = y - origin.y;

		//reflection
		transformedX = transformedX * (flipHorizontal ? -1 : 1);
		transformedY = transformedY * (flipVertical ? -1 : 1);

		const rotationX = transformedX;
		const rotationY = transformedY;
		//rotate (https://en.wikipedia.org/wiki/Rotation_matrix#Rotations_in_two_dimensions)
		transformedX = rotationX * cos(rotation.radians) - rotationY * sin(rotation.radians);
		transformedY = rotationX * sin(rotation.radians) + rotationY * cos(rotation.radians);

		//translate back to original origin
		transformedX = transformedX + origin.x;
		transformedY = transformedY + origin.y;

		return new PointInTransformed(this.coordinateSpace, transformation, round(transformedX, 3), round(transformedY, 3));
	}

	rotate(center: IPoint<TCoordinateSpace>, rotation: Angle) {
		const { x, y } = this;
		const { x: cx, y: cy } = center;
		const { radians } = rotation;

		if (radians === 0 || radians === 2 * Math.PI) return this;

		if (!Number.isFinite(x) || !Number.isFinite(y)) return this;

		const cos = Math.cos(radians);
		const sin = Math.sin(radians);
		const dy = y - cy;
		const dx = x - cx;
		const rotatedX = cos * dx - sin * dy + cx;
		const rotatedY = cos * dy + sin * dx + cy;
		return this.coordinateSpace.createPoint(Math.round(rotatedX), Math.round(rotatedY));
	}

	scale(scale: number) {
		const { x, y } = this;
		return this.coordinateSpace.createPoint(x * scale, y * scale);
	}

	toBasicPoint() {
		return { x: this.x, y: this.y };
	}

	toString() {
		const { x, y } = this;
		return `(${x.toFixed(2)}, ${y.toFixed(2)})`;
	}

	static fromBasicPoint<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(basicPoint: BasicPoint, coordinateSpace: TCoordinateSpace) {
		return coordinateSpace.createPoint(basicPoint.x, basicPoint.y);
	}
}

export class PointImpl<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Point<TCoordinateSpace> {
	readonly kind = "point-in-original" as const;
}

export interface IPointInWindow extends IPoint<InWindow> {}

export interface IPointInMonitorSetup extends IPoint<InMonitorSetup> {}

export interface IPointInVisibleCanvas extends IPoint<InVisibleCanvas> {}

export interface IPointInVisibleCanvasDrawRegion extends IPoint<InVisibleCanvasDrawRegion> {}

export interface IPointInOverviewImage extends IPoint<InOverviewImage> {}

export interface IPointInFullSizeImage extends IPoint<InFullSizeImage> {}

export interface IPointInTransformedFullSizeImage extends IPoint<InTransformed<InFullSizeImage>> {}

/**
 * A point in the coordinate space of the current monitor setup
 */
export class PointInMonitorSetup extends Point<InMonitorSetup> implements IPointInMonitorSetup {
	readonly kind = "point-in-monitor-setup" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inMonitorSetup, x, y);
	}
}

/**
 * A point in the coordinate space of the current HTML window
 */
export class PointInWindow extends Point<InWindow> implements IPointInWindow {
	readonly kind = "point-in-window" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inWindow, x, y);
	}

	static fromReactMouseEvent(e: React.MouseEvent) {
		return new PointInWindow(e.clientX, e.clientY);
	}

	static fromMouseEvent(e: MouseEvent) {
		return new PointInWindow(e.clientX, e.clientY);
	}

	static fromReactPointerEvent(e: React.PointerEvent) {
		return new PointInWindow(e.clientX, e.clientY);
	}

	static fromPointerEvent(e: PointerEvent | React.PointerEvent) {
		return new PointInWindow(e.clientX, e.clientY);
	}

	static fromReactTouch(touch: React.Touch) {
		return new PointInWindow(touch.clientX, touch.clientY);
	}

	static fromTouch(touch: Touch) {
		return new PointInWindow(touch.clientX, touch.clientY);
	}
}

/**
 * Converts this point in the HTML window to the same point in the visible canvas
 */
export const pointInWindowToPointInVisibleCanvas = (visibleCanvasStore: IVisibleCanvasStore) => (point: Point<InWindow>) => {
	const { topLeft, scale } = visibleCanvasStore;

	/* Do not clamp these values, we must support negative/overflowing values */
	const x = Math.round(scale * (point.x - topLeft!.x));
	const y = Math.round(scale * (point.y - topLeft!.y));

	return new PointInVisibleCanvas(x, y);
};

/**
 * A point in the coordinate space of a visible canvas
 * (0,0) denotes the top left pixel of the visible canvas
 */
export class PointInVisibleCanvas extends Point<InVisibleCanvas> implements IPointInVisibleCanvas {
	readonly kind = "point-in-visible-canvas" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inVisibleCanvas, x, y);
	}
}

export const pointInVisibleCanvasToPointInVisibleCanvasDrawRegion = (visibleCanvasDrawRegionStore: IVisibleCanvasDrawRegionStore) => (point: IPoint<InVisibleCanvas>) => {
	const { visibleCanvasDrawRegion } = visibleCanvasDrawRegionStore;
	if (!visibleCanvasDrawRegion) throw new Error("Cannot convert this point to a point in visible canvas draw region because there is no visible canvas draw region");

	try {
		return new PointInVisibleCanvasDrawRegion(point.x - visibleCanvasDrawRegion.topLeft.x, point.y - visibleCanvasDrawRegion.topLeft.y);
	} catch (e) {
		// This try+catch is added for investigation of PAD-5391 and can be removed once diagnosed.
		e.message += ` in pointInVisibleCanvasToPointInVisibleCanvasDrawRegion (point = ${point}, topLeft = ${visibleCanvasDrawRegion.topLeft}).`;
		throw e;
	}
};

/**
 * A point in the coordinate space of the draw region of a visible canvas.
 * (0,0) denotes the top left pixel of the draw region of the visible canvas
 */
export class PointInVisibleCanvasDrawRegion extends Point<InVisibleCanvasDrawRegion> implements IPointInVisibleCanvasDrawRegion {
	readonly kind = "point-in-visible-canvas-draw-region" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inVisibleCanvasDrawRegion, x, y);
	}
}

export const pointInVisibleCanvasDrawRegionToPointInTransformedFullImage =
	(transformedImageRegionsStore: ITransformedImageRegionsStore, visibleCanvasDrawRegionStore: IVisibleCanvasDrawRegionStore) => (point: IPoint<InVisibleCanvasDrawRegion>) => {
		const { currentTransformedFullSizeImageRegion, currentTransformedFullSizeImageRegionTransformation } = transformedImageRegionsStore;
		const { visibleCanvasDrawRegion } = visibleCanvasDrawRegionStore;

		if (!visibleCanvasDrawRegion) throw new Error("Cannot convert this point to a point in transformed full size image region because there is no visible canvas draw region");
		if (!currentTransformedFullSizeImageRegion)
			throw new Error("Cannot convert this point to a point in transformed full size image region because there is no transformed full size image region");

		const currentTransformedFullSizeImageRegionSize = currentTransformedFullSizeImageRegion.size;
		const visibleCanvasDrawRegionSize = visibleCanvasDrawRegion.size;

		const ratio = currentTransformedFullSizeImageRegionSize.width / visibleCanvasDrawRegionSize.width;

		return new PointInTransformed<InFullSizeImage>(
			CoordinateSpace.inFullSizeImage,
			currentTransformedFullSizeImageRegionTransformation,
			Math.floor(ratio * point.x + currentTransformedFullSizeImageRegion.topLeft.x),
			Math.floor(ratio * point.y + currentTransformedFullSizeImageRegion.topLeft.y),
		);
	};

export const pointInVisibleCanvasDrawRegionToPointInVisibleCanvas = (visibleCanvasDrawRegionStore: IVisibleCanvasDrawRegionStore) => (point: IPoint<InVisibleCanvasDrawRegion>) => {
	const { visibleCanvasDrawRegion } = visibleCanvasDrawRegionStore;
	if (!visibleCanvasDrawRegion) throw new Error("Cannot convert this point to a point in visible canvas because there is no visible canvas draw region");

	return new PointInVisibleCanvas(point.x + visibleCanvasDrawRegion.topLeft.x, point.y + visibleCanvasDrawRegion.topLeft.y);
};

/**
 * Converts this point in the visible canvas to the same point in the HTML window
 */
export const pointInVisibleCanvasToPointInWindow = (visibleCanvasStore: IVisibleCanvasStore) => (point: IPointInVisibleCanvas) => {
	const { topLeft, scale } = visibleCanvasStore;

	const x = Math.round(topLeft!.x + point.x / scale);
	const y = Math.round(topLeft!.y + point.y / scale);

	return new PointInWindow(x, y);
};

/**
 * A point in the coordinate space of the overview image
 */
export class PointInOverviewImage extends Point<InOverviewImage> implements IPointInOverviewImage {
	readonly kind = "point-in-overview-image" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inOverviewImage, x, y);
	}
}

/**
 * A point in the coordinate space of the full size image
 */
export class PointInFullSizeImage extends Point<InFullSizeImage> implements IPointInFullSizeImage {
	readonly kind = "point-in-full-size-image" as const;

	constructor(x: number, y: number) {
		super(CoordinateSpace.inFullSizeImage, x, y);
	}
}

/**
 * A point in a transformed coordinate space
 */
export class PointInTransformed<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>> extends Point<InTransformed<TCoordinateSpace>> {
	readonly kind = "point-in-transformed" as const;

	constructor(originalCoordinateSpace: ICoordinateSpace<TCoordinateSpace>, transformation: ITransformation<TCoordinateSpace>, x: number, y: number) {
		super(CoordinateSpace.inTransformed(originalCoordinateSpace, transformation), x, y);
	}
}

export const pointInFullSizeImageToPointInTransformedFullSizeImage =
	() =>
	(point: IPointInFullSizeImage, transformation: ITransformation<InFullSizeImage>): IPointInTransformedFullSizeImage => {
		return point.transform(transformation);
	};

export const pointInTransformedFullSizeImageToPointInFullImage =
	() =>
	(point: PointInTransformed<InFullSizeImage>): IPoint<InFullSizeImage> => {
		return point.coordinateSpace.transformToOriginal(point);
	};

export const pointInTransformedFullImageToPointVisibleCanvasDrawRegion =
	(transformedImageRegionsStore: ITransformedImageRegionsStore, visibleCanvasDrawRegionStore: IVisibleCanvasDrawRegionStore) => (point: IPointInTransformedFullSizeImage) => {
		const { currentTransformedFullSizeImageRegion } = transformedImageRegionsStore;
		const { visibleCanvasDrawRegion } = visibleCanvasDrawRegionStore;

		if (!visibleCanvasDrawRegion) throw new Error("Cannot convert this point to a point in visible canvas draw region because there is no visible canvas draw region");
		if (!currentTransformedFullSizeImageRegion)
			throw new Error("Cannot convert this point to a point in visible canvas draw region because there is no transformed full size image region");

		const currentTransformedFullSizeImageRegionSize = currentTransformedFullSizeImageRegion.size;
		const visibleCanvasDrawRegionSize = visibleCanvasDrawRegion.size;

		const ratio = visibleCanvasDrawRegionSize.width / currentTransformedFullSizeImageRegionSize.width;

		return new PointInVisibleCanvasDrawRegion(
			Math.round(ratio * (point.x - currentTransformedFullSizeImageRegion.topLeft.x)),
			Math.round(ratio * (point.y - currentTransformedFullSizeImageRegion.topLeft.y)),
		);
	};

export const transformPointToOriginal =
	<TCoordinateSpace extends ICoordinateSpace<TCoordinateSpace>>(coordinateSpace: InTransformed<TCoordinateSpace>) =>
	(point: IPoint<InTransformed<TCoordinateSpace>>) => {
		const { cos, sin } = Math;
		const { x, y } = point;
		const { transformation, originalCoordinateSpace } = coordinateSpace;
		const { origin, rotation, flipHorizontal, flipVertical } = transformation;

		if (!Number.isFinite(x) || !Number.isFinite(y)) return originalCoordinateSpace.createPoint(x, y);

		//translate origin to the rotation/flipping origin
		let transformedX = x - origin.x;
		let transformedY = y - origin.y;

		const rotationX = transformedX,
			rotationY = transformedY;
		//rotate (https://en.wikipedia.org/wiki/Rotation_matrix#Rotations_in_two_dimensions)
		transformedX = rotationX * cos(-rotation.radians) - rotationY * sin(-rotation.radians);
		transformedY = rotationX * sin(-rotation.radians) + rotationY * cos(-rotation.radians);

		//reflection
		transformedX = transformedX * (flipHorizontal ? -1 : 1);
		transformedY = transformedY * (flipVertical ? -1 : 1);

		//translate back to original origin

		transformedX = transformedX + origin.x;
		transformedY = transformedY + origin.y;

		return originalCoordinateSpace.createPoint(round(transformedX, 3), round(transformedY, 3));
	};

export class PointInTransformedFullSizeImage extends PointInTransformed<InFullSizeImage> implements IPointInTransformedFullSizeImage {
	public readonly transformation: ITransformation<InFullSizeImage>;

	constructor(transformation: ITransformation<InFullSizeImage>, x: number, y: number) {
		super(CoordinateSpace.inFullSizeImage, transformation, x, y);
		this.transformation = transformation;
	}
}

export function isPointInMonitorSetup(point: IPoint<any>): point is IPoint<InMonitorSetup> {
	return point.coordinateSpace.kind === CoordinateSpace.inMonitorSetup.kind;
}

export function isPointInWindow(point: IPoint<any>): point is IPoint<InWindow> {
	return point.coordinateSpace.kind === CoordinateSpace.inWindow.kind;
}

export function isPointInVisibleCanvas(point: IPoint<any>): point is IPoint<InVisibleCanvas> {
	return point.coordinateSpace.kind === CoordinateSpace.inVisibleCanvas.kind;
}

export function isPointInVisibleCanvasDrawRegion(point: IPoint<any>): point is IPoint<InVisibleCanvasDrawRegion> {
	return point.coordinateSpace.kind === CoordinateSpace.inVisibleCanvasDrawRegion.kind;
}

export function isPointInOverviewImage(point: IPoint<any>): point is IPoint<InOverviewImage> {
	return point.coordinateSpace.kind === CoordinateSpace.inOverviewImage.kind;
}

export function isPointInFullSizeImage(point: IPoint<any>): point is IPoint<InFullSizeImage> {
	return point.coordinateSpace.kind === CoordinateSpace.inFullSizeImage.kind;
}

export function isPointInTransformed(point: IPoint<any>): point is IPoint<InTransformed<InFullSizeImage>> {
	return point.coordinateSpace.kind === "in-transformed";
}
